import Link from "next/link";
import Image from "next/image";
import {
  PanelRight,
  ArrowRightToLine,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useSidenavOpenState } from "./useSidenavOpenState";

import { EnvService } from "@/services/Environment/Environment";
import { useSpotUser } from "@/utils/spotUser";
import { useParams, usePathname } from "next/navigation";
import { Dispatch, SetStateAction, useMemo, useState, type JSX } from "react";
import { useAtom } from "jotai";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useRemoteData } from "@/utils/useRemoteData";
import { formatTime } from "@/utils/generic";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { Button } from "@/components/shadcn/button";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/shadcn/separator";
import { AnimatePresence, motion } from "framer-motion";
import { atom } from "jotai";
import { ConversationsService } from "@/generated";
import { SpotToast } from "@/components/SpotToast";
import { Skeleton } from "@/components/shadcn/skeleton";
import { useDeploymentApp } from "@/services/Deployments/useDeploymentApp";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { Input } from "@/components/shadcn/input";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DeleteDialog } from "@/components/Modals/DeleteDialog";
import { usePermissions } from "@/hooks/usePermissions";
import { useTranslations } from "next-intl";
import { WORKSPACE_PERMISSIONS } from "@/constants/permissions";

export const activeChatAtom = atom<
  | { id: string; assistant_id: string; assistant_version_id?: string }
  | undefined
>(undefined);
export const activeRunAtom = atom<
  { id: string; workflow_id: string } | undefined
>(undefined);

// Per-deployment override for the sidebar logo. Deployment pages set this on
// mount (and clear on unmount) to replace the tenant/whitelabel logo while
// the user is inside an app.
export const deploymentSidebarLogoAtom = atom<string | undefined>(undefined);

interface IEntry {
  title: string;
  icon: JSX.Element;
  href?: string;
  onClick?: any;
  tag?: string;
  permissions?: string[];
  dropdownEntries?: IEntry[];
  isOpenByDefault?: boolean;
  adminOnly?: boolean;
  id: string;
  is_enterprise_locked?: boolean;
}

interface ISidenavConfig {
  topEntries?: IEntry[];
  bottomEntries?: IEntry[];
  header?: JSX.Element;
  footer?: JSX.Element;
}

const DashboardHeader = ({
  isCollapsed,
  isCollapsible,
  setIsCollapsible,
}: {
  isCollapsed: boolean;
  isCollapsible: boolean;
  setIsCollapsible: Dispatch<SetStateAction<boolean>>;
}) => {
  const t = useTranslations("app");
  const whitelabel_image = EnvService.getVar("WHITELABEL_LOGO");
  const [deploymentLogo] = useAtom(deploymentSidebarLogoAtom);

  const toplogo = deploymentLogo || whitelabel_image;

  const handleCollapse = () => {
    setIsCollapsible((prev) => !prev);
  };

  return (
    <div className="w-full flex justify-between items-center h-4 flex-shrink-0">
      <Link
        href="/"
        className="relative cursor-pointer h-4 flex items-center justify-start"
      >
        {!toplogo && (
          <>
            <div className="absolute left-4 h-full">
              <Image
                src="https://storage.googleapis.com/image-storage-spot-manual/app/sidenav/noxus-o.svg"
                alt={"Noxus logo"}
                style={{ height: "100%", width: "auto" }}
                width={17}
                height={16}
                className="dark:invert dark:brightness-0"
              />
            </div>
            <div
              className="h-full min-w-18.5 transition-opacity duration-250"
              style={{ opacity: isCollapsed ? 0 : 1 }}
            >
              <Image
                src="https://storage.googleapis.com/image-storage-spot-manual/app/sidenav/nxus.svg"
                alt={"Noxus text logo"}
                style={{ height: "100%", width: "auto" }}
                width={75}
                height={16}
                className="dark:invert dark:brightness-0"
              />
            </div>
          </>
        )}
        {toplogo && (
          <>
            <div
              className="absolute h-full w-14.5 transition-opacity duration-250"
              style={{ opacity: isCollapsed ? 1 : 0 }}
            >
              <Image
                src={toplogo}
                alt={"logo"}
                objectFit="contain"
                objectPosition="top left"
                fill
                style={{
                  minHeight: "24px",
                  minWidth: "24px",
                }}
                className="dark:invert dark:brightness-0"
              />
            </div>
            <div
              className="h-full min-w-18.5 transition-opacity duration-250"
              style={{ opacity: isCollapsed ? 0 : 1 }}
            >
              <Image
                src={toplogo}
                alt={"Noxus text logo"}
                objectFit="contain"
                objectPosition="top left"
                fill
                style={{ minHeight: "24px" }}
                className="dark:invert dark:brightness-0"
              />
            </div>
          </>
        )}
      </Link>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCollapse}
            aria-label={isCollapsible ? t("pinSidebar") : t("unpinSidebar")}
            data-testid="sidenav-pin-toggle"
            className={cn(isCollapsed && "hidden", "absolute right-2 top-2")}
          >
            {isCollapsible ? (
              <ArrowRightToLine size="1.25rem" strokeWidth="1.25px" />
            ) : (
              <PanelRight size="1.25rem" strokeWidth="1.25px" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isCollapsible ? t("pinSidebar") : t("unpinSidebar")}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

export { isCollapsibleAtom } from "./atoms";

/* This component was created separate from SettingsSideNav because of its collapsible feature */
export const AppsSideNav = () => {
  const { attach, open, isCollapsible, setIsCollapsible, setIsHovered } =
    useSidenavOpenState();

  const isCollapsed = !open;

  return (
    <div
      ref={attach}
      className="relative flex flex-col h-full py-4 transition-all duration-250 gap-4"
      style={{
        paddingLeft: isCollapsed ? "0.25rem" : "1rem",
        paddingRight: isCollapsed ? "0.25rem" : "1rem",
        width: isCollapsed ? "0rem" : "16rem",
        opacity: isCollapsed ? 0 : 1,
        overflow: isCollapsed ? "hidden" : undefined,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <DashboardHeader
        isCollapsed={isCollapsed}
        isCollapsible={isCollapsible}
        setIsCollapsible={setIsCollapsible}
      />
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            key="app-history"
            className="flex-1 min-h-0"
          >
            <AppHistory />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AppHistory = () => {
  const pathname = usePathname();

  const isFlowApp = pathname?.includes("/apps/f/");
  const isCoworkerApp = pathname?.includes("/apps/c/");
  const isDeploymentApp = pathname?.includes("/apps/d/");

  if (!isFlowApp && !isCoworkerApp && !isDeploymentApp) {
    return null;
  }

  if (isFlowApp) {
    return <FlowAppHistory />;
  }
  if (isCoworkerApp) {
    return <ChatAppHistory />;
  }
  if (isDeploymentApp) {
    return <DeploymentChatHistory />;
  }

  return null;
};

export const ConversationEntry = ({
  conversation,
  isActive,
  onClick,
  groupId,
  onDelete,
  showTooltip = true,
  activeClassName = "bg-sidebar-accent",
  hoverClassName = "hover:bg-sidebar-accent/50",
  className,
  children,
}: {
  conversation: { id: string; name: string; created_at?: string };
  isActive: boolean;
  onClick: () => void;
  groupId: string;
  onDelete?: () => void;
  showTooltip?: boolean;
  activeClassName?: string;
  hoverClassName?: string;
  className?: string;
  children?: React.ReactNode;
}) => {
  const t = useTranslations("app");
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editName, setEditName] = useState(conversation.name || "");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const queryClient = useQueryClient();

  const renameMutation = useMutation({
    mutationFn: (name: string) =>
      ConversationsService.updateConversation({
        path: { group_id: groupId, conversation_id: conversation.id },
        body: { name },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.CONVERSATIONS] });
      setIsEditing(false);
    },
    onError: () => {
      SpotToast.error(t("renameFailed"));
      setEditName(conversation.name || "");
      setIsEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      ConversationsService.updateConversation({
        path: { group_id: groupId, conversation_id: conversation.id },
        body: { is_deleted: true },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.CONVERSATIONS] });
      SpotToast.success(t("deleted"));
      setIsDeleteDialogOpen(false);
      if (isActive && onDelete) {
        onDelete();
      }
    },
    onError: () => {
      SpotToast.error(t("deleteFailed"));
    },
  });

  const handleSaveRename = () => {
    const trimmedName = editName.trim();
    if (trimmedName && trimmedName !== conversation.name) {
      renameMutation.mutate(trimmedName);
    } else {
      setEditName(conversation.name || "");
      setIsEditing(false);
    }
  };

  const handleCancelRename = () => {
    setEditName(conversation.name || "");
    setIsEditing(false);
  };

  const content = (
    <div
      className={cn(
        "group relative flex items-center w-full rounded-md",
        hoverClassName,
        isActive && [activeClassName, "hover:bg-sidebar-accent"],
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        if (!isDropdownOpen) {
          setIsHovered(false);
        }
      }}
    >
      {isEditing ? (
        <Input
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onBlur={handleSaveRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSaveRename();
            } else if (e.key === "Escape") {
              handleCancelRename();
            }
          }}
          autoFocus
          className="text-sm px-2"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <>
          <Button
            onClick={onClick}
            variant="ghost"
            className={cn(
              "text-left w-full justify-start px-2 hover:bg-transparent"
            )}
          >
            <span className="text-sm text-foreground truncate whitespace-nowrap font-normal pr-6">
              {conversation.name || t("untitledChat")}
            </span>
            {children}
          </Button>

          <div
            className={cn(
              "absolute right-2 top-1 transition-opacity",
              isHovered || isDropdownOpen ? "opacity-100" : "opacity-0"
            )}
          >
            <DropdownMenu
              open={isDropdownOpen}
              onOpenChange={(open) => {
                setIsDropdownOpen(open);
                if (!open) {
                  setIsHovered(false);
                }
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditName(conversation.name || "");
                    setIsEditing(true);
                    setIsDropdownOpen(false);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                  {t("rename")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDeleteDialogOpen(true);
                    setIsDropdownOpen(false);
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                  {t("delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      {showTooltip && conversation.created_at ? (
        <Tooltip delayDuration={500}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>
            <p>{formatTime(conversation.created_at)}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        content
      )}

      <DeleteDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={() => deleteMutation.mutate()}
        title={t("deleteConversationTitle")}
        text={t("deleteConversationText")}
      />
    </>
  );
};

const ConversationSearchBar = ({
  open,
  onOpenChange,
  value,
  onValueChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onValueChange: (v: string) => void;
}) => {
  const t = useTranslations("app");
  return (
    <motion.div
      layout
      transition={{ duration: 0.2, ease: "easeOut" }}
      onClick={() => {
        if (!open) onOpenChange(true);
      }}
      className={cn(
        "relative flex items-center w-full h-9 rounded-md border",
        open
          ? "border-foreground/30 cursor-text"
          : "border-transparent hover:bg-sidebar-accent/50 cursor-pointer"
      )}
    >
      <motion.div layout className="absolute left-2 flex items-center">
        <Search className="h-4 w-4 text-foreground" />
      </motion.div>
      <AnimatePresence mode="wait" initial={false}>
        {open ? (
          <motion.div
            key="input"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex-1 pl-8 pr-8"
          >
            <Input
              autoFocus
              value={value}
              onChange={(e) => onValueChange(e.target.value)}
              placeholder={t("searchPlaceholder")}
              className="h-9 px-0 text-sm bg-transparent border-0 shadow-none focus-visible:ring-0 placeholder:text-muted-foreground/60"
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  onValueChange("");
                  onOpenChange(false);
                }
              }}
              onBlur={() => {
                if (!value) onOpenChange(false);
              }}
            />
          </motion.div>
        ) : (
          <motion.span
            key="label"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="pl-8 text-sm text-foreground"
          >
            {t("searchChats")}
          </motion.span>
        )}
      </AnimatePresence>
      {open && value && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            onValueChange("");
            onOpenChange(false);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </motion.div>
  );
};

const ChatAppHistory = () => {
  const t = useTranslations("app");
  const { spotUser, spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const params = useParams<{ app_id: string }>();
  const appId = params?.app_id;
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const { data: runsData, isLoading: isRunsLoading } = useRemoteData(
    DATA_KEYS.CONVERSATIONS,
    {
      path: {
        group_id: spotGroup.id,
      },
      query: {
        assistant_id: appId || "",
        user_id: spotUser.id,
      },
    },
    {
      enabled: !!appId,
      retry: false,
    }
  );

  const {
    data: assistantData,
    isError: isAssistantError,
    isLoading: isAssistantLoading,
  } = useRemoteData(
    DATA_KEYS.ASSISTANT_ID,
    {
      path: { group_id: spotGroup.id, assistant_id: appId || "" },
    },
    { enabled: !!appId, retry: false }
  );

  const createNewConversation = async () => {
    if (!appId) return;

    try {
      const conversation = await ConversationsService.createConversation({
        path: { group_id: spotGroup.id },
        body: {
          name: t("newChat"),
          assistant_version_id: assistantData?.app_settings_v2?.version_id,
        },
        query: {
          assistant_id: appId,
        },
      });
      setActiveChatId({ id: conversation.data.id, assistant_id: appId });
    } catch (error) {
      SpotToast.error("Error creating conversation.");
    }
  };

  const [activeChatId, setActiveChatId] = useAtom(activeChatAtom);

  const handleChatClick = (chatId: string | undefined) => {
    if (!chatId || !appId) return;
    queryClient.removeQueries({
      queryKey: [
        DATA_KEYS.CONVERSATION,
        { path: { group_id: spotGroup.id, conversation_id: chatId } },
      ],
    });
    setActiveChatId({ id: chatId, assistant_id: appId });
  };

  const {
    hasAnyPermission,
    hasAllPermissions,
    isLoading: isLoadingPermissions,
  } = usePermissions();

  const user_has_permission = useMemo(() => {
    if (!assistantData) return false;

    if (
      hasAnyPermission(
        [WORKSPACE_PERMISSIONS.EDIT, WORKSPACE_PERMISSIONS.DELETE],
        "group"
      )
    )
      return true;

    if (!hasAnyPermission([WORKSPACE_PERMISSIONS.RUN], "group")) {
      return false;
    }

    if (!assistantData.app_settings_v2?.extra_settings) return true;

    if (assistantData.app_settings_v2?.extra_settings?.allow_all_users)
      return true;
    if (
      (
        assistantData.app_settings_v2?.extra_settings?.allowed_users as
          | string[]
          | undefined
      )?.includes(spotUser.id)
    )
      return true;

    return false;
  }, [assistantData, spotUser.id, hasAnyPermission]);

  if (assistantData && !assistantData.app_settings_v2?.is_active) {
    return null;
  }

  if (isAssistantError) {
    return null;
  }

  if (!user_has_permission) {
    return null;
  }

  if (isAssistantLoading || isRunsLoading) {
    return <Skeleton className="w-full h-10" />;
  }

  if (!runsData || !runsData.items) {
    return (
      <div className="flex flex-col gap-2 w-full">
        <p className="text-sm text-gray-500 truncate whitespace-nowrap">
          No previous chats
        </p>
      </div>
    );
  }

  const hasChats = runsData.items.length > 0;

  return (
    <div className="flex flex-col gap-1 w-full h-full">
      <Button
        variant="ghost"
        onClick={createNewConversation}
        className="w-full justify-start px-2 hover:bg-sidebar-accent/50 font-normal"
      >
        <Plus className="h-4 w-4" />
        <span className="text-sm text-foreground">{t("newChat")}</span>
      </Button>
      <ConversationSearchBar
        open={searchOpen}
        onOpenChange={setSearchOpen}
        value={searchText}
        onValueChange={setSearchText}
      />
      <p className="text-xs text-foreground mt-5 mb-1 py-1 px-2 truncate whitespace-nowrap">
        {t("previousChats")}
      </p>
      {hasChats ? (
        <div className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto py-1">
          {runsData.items
            .filter((run: any) =>
              (run.name || "")
                .toLowerCase()
                .includes(searchText.trim().toLowerCase())
            )
            .map((run: any) => (
              <ConversationEntry
                key={run.id}
                conversation={run}
                isActive={activeChatId?.id === run.id}
                onClick={() => handleChatClick(run.id)}
                groupId={spotGroup.id}
                onDelete={createNewConversation}
                showTooltip={false}
              />
            ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground px-2 py-1">
          {t("noChatsYet")}
        </p>
      )}
    </div>
  );
};

const DeploymentChatHistory = () => {
  const t = useTranslations("app");
  const { spotUser, spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const params = useParams<{ deployment_id: string }>();
  const deploymentRef = params?.deployment_id;
  const [activeChatId, setActiveChatId] = useAtom(activeChatAtom);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Resolve the assistant from the deployment rather than from activeChatAtom:
  // past chats have to be listable before a conversation exists.
  const {
    deploymentApp,
    deploymentId,
    isLoading: isDeploymentLoading,
  } = useDeploymentApp(deploymentRef);

  const assistantId = activeChatId?.assistant_id ?? deploymentApp?.assistant_id;
  const assistantVersionId =
    activeChatId?.assistant_version_id ??
    deploymentApp?.assistant_version_id ??
    undefined;

  const { data: runsData, isLoading: isRunsLoading } = useRemoteData(
    DATA_KEYS.CONVERSATIONS,
    {
      path: { group_id: spotGroup.id },
      query: {
        assistant_id: assistantId || "",
        user_id: spotUser.id,
      },
    },
    {
      enabled: !!assistantId,
      retry: false,
    }
  );

  const createNewConversation = async () => {
    if (!assistantId) return;
    try {
      const conversation = await ConversationsService.createConversation({
        path: { group_id: spotGroup.id },
        body: {
          name: t("newChat"),
          assistant_version_id: assistantVersionId,
        },
        query: { assistant_id: assistantId, deployment_id: deploymentId },
      });
      setActiveChatId({
        id: conversation.data.id,
        assistant_id: assistantId,
        assistant_version_id: assistantVersionId,
      });
    } catch {
      SpotToast.error("Error creating conversation.");
    }
  };

  const handleChatClick = (chatId: string | undefined) => {
    if (!chatId || !assistantId) return;
    queryClient.removeQueries({
      queryKey: [
        DATA_KEYS.CONVERSATION,
        { path: { group_id: spotGroup.id, conversation_id: chatId } },
      ],
    });
    setActiveChatId({
      id: chatId,
      assistant_id: assistantId,
      assistant_version_id: assistantVersionId,
    });
  };

  if (isDeploymentLoading || (!!assistantId && isRunsLoading)) {
    return <Skeleton className="w-full h-10" />;
  }

  if (!assistantId) {
    return (
      <p className="text-sm text-gray-500 truncate whitespace-nowrap">
        No previous chats
      </p>
    );
  }

  const hasChats = runsData?.items && runsData.items.length > 0;

  return (
    <div className="flex flex-col gap-1 w-full h-full">
      <Button
        variant="ghost"
        onClick={createNewConversation}
        className="w-full justify-start px-2 hover:bg-sidebar-accent/50 font-normal"
      >
        <Plus className="h-4 w-4" />
        <span className="text-sm text-foreground">{t("newChat")}</span>
      </Button>
      <ConversationSearchBar
        open={searchOpen}
        onOpenChange={setSearchOpen}
        value={searchText}
        onValueChange={setSearchText}
      />
      <p className="text-xs text-foreground mt-5 mb-1 py-1 px-2 truncate whitespace-nowrap">
        {t("previousChats")}
      </p>
      {hasChats ? (
        <div className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto py-1">
          {runsData.items
            .filter((run: any) =>
              (run.name || "")
                .toLowerCase()
                .includes(searchText.trim().toLowerCase())
            )
            .map((run: any) => (
              <ConversationEntry
                key={run.id}
                conversation={run}
                isActive={activeChatId?.id === run.id}
                onClick={() => handleChatClick(run.id)}
                groupId={spotGroup.id}
                onDelete={createNewConversation}
                showTooltip={false}
              />
            ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground px-2 py-1">
          {t("noChatsYet")}
        </p>
      )}
    </div>
  );
};

const FlowAppHistory = () => {
  const { spotUser, spotGroup } = useSpotUser();
  const params = useParams<{ app_id: string }>();
  const appId = params?.app_id;

  const { hasAnyPermission, isLoading: isLoadingPermissions } =
    usePermissions();

  const { data: workflowData, isError } = useRemoteData(
    DATA_KEYS.WORKFLOWS_ID,
    {
      path: { group_id: spotGroup.id, workflow_id: appId || "" },
    },
    { enabled: !!appId, retry: false }
  );

  const { data: runsData } = useRemoteData(
    DATA_KEYS.WORKFLOW_RUNS,
    {
      path: {
        group_id: spotGroup.id,
        workflow_id: appId || "",
      },
      query: {
        order_by: "created_at",
        sort: "desc",
        user_id: spotUser.id,
        source: "app",
      },
    },
    {
      enabled: !!appId,
      retry: false,
    }
  );

  const [activeRunId, setActiveRunId] = useAtom(activeRunAtom);

  const handleRunClick = (runId: string | undefined) => {
    if (!runId) {
      setActiveRunId(undefined);
      return;
    }
    if (!appId) return;
    setActiveRunId({ id: runId, workflow_id: appId });
  };

  const user_has_permission = useMemo(() => {
    if (!workflowData) return false;

    if (spotUser.is_admin) return true;
    if (
      hasAnyPermission(
        [WORKSPACE_PERMISSIONS.EDIT, WORKSPACE_PERMISSIONS.DELETE],
        "group"
      )
    )
      return true;

    if (!hasAnyPermission([WORKSPACE_PERMISSIONS.RUN], "group")) {
      return false;
    }

    if (!workflowData.app_settings_v2?.extra_settings) return true;

    if (workflowData.app_settings_v2?.extra_settings?.allow_all_users)
      return true;
    if (
      (
        workflowData.app_settings_v2?.extra_settings?.allowed_users as
          | string[]
          | undefined
      )?.includes(spotUser.id)
    )
      return true;

    return false;
  }, [workflowData, spotUser.is_admin, spotUser.id, hasAnyPermission]);

  if (workflowData && !workflowData.app_settings_v2?.is_active) {
    return null;
  }

  if (isError) {
    return null;
  }

  if (isLoadingPermissions) {
    return <Skeleton className="w-full h-10" />;
  }

  if (!user_has_permission) {
    return null;
  }

  if (!runsData) {
    return (
      <div className="flex flex-col gap-2 w-full">
        <p className="text-sm text-gray-500 truncate whitespace-nowrap">
          No recent runs
        </p>
      </div>
    );
  }

  const hasRuns = runsData.items.length > 0;

  return (
    <div className="flex flex-col gap-1 w-full h-full">
      <Button
        variant="ghost"
        data-testid="app-new-run"
        onClick={() => handleRunClick(undefined)}
        className="w-full justify-start px-2 hover:bg-sidebar-accent/50 font-normal"
      >
        <Plus className="h-4 w-4" />
        <span className="text-sm text-foreground">New run</span>
      </Button>
      <p className="text-xs text-foreground mb-1 py-1 mt-5 px-2 truncate whitespace-nowrap">
        Recent runs
      </p>
      {hasRuns ? (
        <div className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto">
          {runsData.items.map((run: any) => (
            <Button
              key={run.id}
              data-testid="app-run-item"
              data-run-id={run.id}
              onClick={() => handleRunClick(run.id)}
              variant="ghost"
              className={cn(
                "hover:bg-sidebar-accent/50 text-left w-full justify-start px-2",
                activeRunId?.id === run.id && [
                  "bg-sidebar-accent",
                  "hover:bg-sidebar-accent",
                ]
              )}
            >
              <span className="text-sm text-foreground truncate whitespace-nowrap font-normal">
                {formatTime(run.created_at)}
              </span>
            </Button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground px-2 py-1">
          You haven&apos;t started any runs yet.
        </p>
      )}
    </div>
  );
};
